import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  declineRemoteConduct,
  identifyAuditee,
  readAuditeeSession,
  readPortal,
  setAccessLink,
  uploadPortalDocuments,
  type SessionState,
  type UploadOutcome,
  type PortalView,
} from "../api/client";
import { Acknowledgement } from "../components/Acknowledgement";
import { Callout } from "../components/Callout";
import { TextAreaField } from "../components/TextAreaField";
import { TextField } from "../components/TextField";
import { AuditeeSession, BeginSessionDialog } from "../session/AuditeeSession";

/**
 * The auditee's portal — screens 45 to 47, and the audit-day gate on 50 and 51.
 *
 * **The one participant with no account.** The auditee holds a link to one audit; everyone
 * else signs in. That asymmetry is deliberate — an audit client should not have to be
 * onboarded onto a certification body's platform to be audited — and it is why this screen
 * lives outside the app shell and carries its own credential.
 *
 * **What the client sees of the plan is not what the certification body sees.** Objectives,
 * scope, criteria, method, the lead auditor and the agenda, and no hours, no percentages and
 * no adjustments. Time allocations and commercial terms are not the client's business, and
 * the server does not send them.
 *
 * **Review results are withheld until the session opens** — and not by this screen. A
 * restrictive policy removes withheld rows from the auditee's transaction, so a route added
 * here later that forgot the rule still cannot show one.
 */
export function AuditeePortal({ token }: { token: string }) {
  const [view, setView] = useState<PortalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  // The session as last read — `null` while unknown or genuinely absent. Kept apart from
  // `viewingSession` below: whether a session *exists* and whether the client is currently
  // *looking at it* are different facts, since M14a+ lets the client step back to the
  // portal from a session that is still running (screen 51's "Portal" link).
  const [session, setSession] = useState<SessionState | null>(null);
  // Explicit, never derived. Auto-true only on the very first read, and only when the
  // client has already joined — a session the lead auditor started but nobody has joined
  // yet shows the dialog instead (`awaitingJoin` below), and a session merely left via
  // "Portal" is picked back up with "Go to session", not silently re-entered.
  const [viewingSession, setViewingSession] = useState(false);
  const [starting, setStarting] = useState(false);

  const refreshSession = () =>
    readAuditeeSession()
      .then(setSession)
      .catch(() => setSession(null));

  useEffect(() => {
    setAccessLink(token);
    readPortal()
      .then(setView)
      .catch((cause: unknown) => setError(problem(cause)));
    readAuditeeSession()
      .then((state) => {
        setSession(state);
        if (state.session_id !== null && (state.participants ?? []).length > 0) {
          setViewingSession(true);
        }
      })
      .catch(() => setSession(null));
  }, [token]);

  // `null` while unknown; `false` once a read completes and finds nothing running.
  const noSession = session === null || session.session_id === null;
  // A session the lead auditor opened ahead of the client — see M14a+'s `session/start`.
  // Nobody is in `session_participants` yet, which is the one fact the client's own arrival
  // is missing from an otherwise-open session.
  const awaitingJoin = !noSession && (session?.participants ?? []).length === 0;

  if (error && !view) {
    return (
      <div className="auditee-shell">
        <p className="problem" role="alert">
          {error}
        </p>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="auditee-shell">
        <p className="sub">Opening your audit&hellip;</p>
      </div>
    );
  }

  // Screen 45. The link lands here until the client has said who they are, because the
  // attendance record and the Cl. 4.1.2 consent both need a name and neither can be
  // supplied on their behalf.
  if (view.identified_as === null) {
    return <Landing view={view} onIdentified={setView} />;
  }

  // Screens 53 to 57 and 67. The session is the whole screen once the client is viewing it:
  // the portal's cards are what they read *before* the audit day or after stepping back from
  // a running one, and leaving them underneath a live audit would put the plan and the
  // transcript on one page.
  if (viewingSession) {
    return (
      <AuditeeSession
        // The organisation is no longer handed down: the console's banner is the screen's
        // only header now, and it names the organisation from the session state itself.
        //
        // The view is re-read rather than reused. Leaving is normally the client stepping
        // back to their documents, where nothing has moved — but a restart also lands here,
        // and it clears the Cl. 4.1.2 consent, so a stale view would show them the portal of
        // an audit they are no longer identified against. The session is re-read too, rather
        // than assumed gone: a deliberate step back to "Portal" leaves it running, and
        // `AuditDay` below needs to know that to offer "Go to session" instead of "Begin
        // audit session".
        onLeave={() => {
          setViewingSession(false);
          void refreshSession();
          readPortal().then(setView).catch(() => undefined);
        }}
      />
    );
  }

  return (
    <div className="auditee-shell">
      <div className="between" style={{ marginBottom: "16px" }}>
        <div>
          <div className="eyebrow">Audit portal</div>
          <h1>Stage 1 audit — {view.client_organisation}</h1>
          <p className="sub">
            <span className="mono">{view.reference}</span> · {view.standards.join(" · ")} ·
            audit date {view.audit_date}
          </p>
        </div>
        <div>
          {view.ict_declined_reason ? (
            <span className="pill p-red">Remote conduct declined</span>
          ) : !noSession ? (
            <span className="pill p-ok">Session in progress</span>
          ) : view.audit_day ? (
            <span className="pill p-ok">Ready to begin</span>
          ) : (
            <span className="pill p-wait">Documents due before audit day</span>
          )}
        </div>
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <AuditDay
        view={view}
        noSession={noSession}
        awaitingJoin={awaitingJoin}
        onBegin={() => setStarting(true)}
        onGoToSession={() => setViewingSession(true)}
        onDecline={() => setDeclining(true)}
        onChanged={setView}
        onError={setError}
      />

      <div className="card">
        <h2>What happens next</h2>
        <ol style={{ paddingLeft: "20px", fontSize: "13px", lineHeight: 1.9 }}>
          <li>Upload the documents listed below before {view.audit_date}.</li>
          <li>On the audit day, open this link again to start the session.</li>
          <li>
            Complao will conduct the audit through conversation, clause by clause.{" "}
            {view.plan?.lead_auditor ?? "Your lead auditor"} is present throughout and may
            join at any point.
          </li>
          <li>
            The formal audit decision and report come from{" "}
            {view.plan?.lead_auditor ?? "your lead auditor"} after internal review — not
            from the session.
          </li>
        </ol>
      </div>

      <Documents view={view} onChanged={setView} onError={setError} />

      <div className="card">
        <h2>Review status</h2>
        {(view.released_reviews ?? []).length === 0 ? (
          <Callout tone="warn" title="Your documents are being reviewed.">
            Findings and gap analysis are withheld until the audit session opens on{" "}
            {view.audit_date}. This is deliberate: Stage 1 assesses your system as it
            stands, so results are shared with you live during the audit rather than in
            advance.
          </Callout>
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Clause</th>
                  <th>Observation</th>
                </tr>
              </thead>
              <tbody>
                {(view.released_reviews ?? []).map((review, index) => (
                  <tr key={index}>
                    <td className="mono">Cl. {String(review.clause_ref ?? "—")}</td>
                    <td className="sub">{String(review.observation ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {view.plan ? <ApprovedPlan view={view} /> : null}

      {starting ? (
        <BeginSessionDialog
          contactName={view.identified_as ?? ""}
          contactRole={view.identified_role ?? ""}
          joining={awaitingJoin}
          onCancel={() => setStarting(false)}
          onOpened={(state) => {
            setStarting(false);
            setSession(state);
            setViewingSession(true);
          }}
        />
      ) : null}

      {declining ? (
        <DeclineDialog
          onCancel={() => setDeclining(false)}
          onConfirm={async (reason) => {
            try {
              setView(await declineRemoteConduct(reason));
              setDeclining(false);
            } catch (cause) {
              setError(problem(cause));
            }
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Screen 45 — the attendance record, and the consent in the client's own name.
 *
 * The certification body records at intake that ICT use was agreed. That is the body's
 * assertion *on the client's behalf*, and Cl. 4.1.2 requires the use of ICT to be
 * **mutually** agreed — so the gate is not satisfied until the client has said so
 * themselves, and the record carries the name that said it.
 */
function Landing({
  view,
  onIdentified,
}: {
  view: PortalView;
  onIdentified: (view: PortalView) => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [consent, setConsent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onIdentified(await identifyAuditee(name, role, consent));
    } catch (cause) {
      setError(problem(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auditee-shell">
      <div className="eyebrow">Secure access link</div>
      <h1>Stage 1 audit — {view.client_organisation}</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Reference <span className="mono">{view.reference}</span> · audit date{" "}
        {view.audit_date} · conducted by {view.certification_body}
      </p>

      <form className="card" onSubmit={submit} noValidate>
        <h2>Confirm who you are</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          Your name and role are recorded in the attendance record for the opening meeting.
          This link is valid for this audit only and stops working once the session closes.
        </p>

        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : null}

        <div className="grid2">
          <TextField id="auditee-name" label="Your full name" value={name} onChange={setName} required />
          <TextField id="auditee-role" label="Your role" value={role} onChange={setRole} required />
        </div>

        <Acknowledgement
          id="auditee-ict"
          title="ICT consent — IAF MD 4:2025."
          checked={consent}
          onChange={setConsent}
        >
          I consent to this Stage 1 audit being conducted remotely using information and
          communication technology.
        </Acknowledgement>

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn" type="submit" disabled={busy || !name.trim() || !role.trim()}>
            {busy ? "Recording…" : "Continue to the audit portal"}
          </button>
        </div>
      </form>

      <Callout tone="warn" title="Why this is asked here.">
        The certification body records that consent was obtained at intake. This screen
        captures it again in your own name, so the audit file holds the client&rsquo;s own
        consent rather than an assertion on the client&rsquo;s behalf.
      </Callout>
    </div>
  );
}

/** Screens 50 and 51 — the audit-day gate, and the way out of it. */
function AuditDay({
  view,
  noSession,
  awaitingJoin,
  onBegin,
  onGoToSession,
  onDecline,
}: {
  view: PortalView;
  //: Whether a session exists at all, from the portal's own re-checked read — not merely
  //: whether the screen happens to be showing one. A client who stepped back to "Portal"
  //: from a running session reads `noSession === false` here, which is the one fact that
  //: used to be discarded on the way back (M14a+).
  noSession: boolean;
  //: A session the lead auditor opened ahead of the client — nobody has joined it yet.
  awaitingJoin: boolean;
  onBegin: () => void;
  onGoToSession: () => void;
  onDecline: () => void;
  onChanged: (view: PortalView) => void;
  onError: (message: string) => void;
}) {
  if (view.ict_declined_reason) {
    return (
      <div className="card">
        <h2>Audit day — {view.audit_date}</h2>
        <Callout tone="block" title="Remote conduct declined.">
          You have told us this audit cannot proceed remotely, and{" "}
          {view.certification_body} has been notified. IAF MD 4:2025 Cl. 4.1.3 requires the
          certification body to use another method to meet the audit objectives — they will
          be in touch to arrange it. This does not affect your certification.
        </Callout>
        <p className="sub">Recorded reason: {view.ict_declined_reason}</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Audit day — {view.audit_date}</h2>
      {!noSession ? (
        <Callout tone="info" title="Your session is running.">
          {awaitingJoin
            ? `${view.plan?.lead_auditor ?? "Your lead auditor"} has already opened the session. Join it to confirm who is attending and take part.`
            : "Reopen it below to pick up where you left off."}
        </Callout>
      ) : view.audit_day ? (
        <Callout tone="info" title="Your session can begin.">
          Complao will lead the audit through this chat.{" "}
          {view.plan?.lead_auditor ?? "Your lead auditor"} is present throughout and may
          join at any point.
        </Callout>
      ) : (
        <Callout tone="warn" title="Not yet.">
          Your session opens on {view.audit_date}. Until then, please send any documents
          relevant to the audit.
        </Callout>
      )}

      <dl className="kv" style={{ marginBottom: "14px" }}>
        <dt>Documents sent</dt>
        <dd>{view.documents_received}</dd>
        <dt>Your access</dt>
        <dd>Stays valid until the audit closes, including across any pauses</dd>
      </dl>

      <div className="row" style={{ gap: "10px" }}>
        {/* Screen 50's two buttons, plus the two states M14a+ adds: a session already
            joined is rejoined rather than reopened, and one the lead auditor started ahead
            of the client is joined rather than begun. Beginning a fresh one is still
            offered only on the audit day; the second button is offered on every day, since
            a client who cannot proceed remotely learns it before the morning of the audit
            as often as on it. */}
        {!noSession && !awaitingJoin ? (
          <button type="button" className="btn" onClick={onGoToSession}>
            Go to session
          </button>
        ) : awaitingJoin ? (
          <button type="button" className="btn" onClick={onBegin}>
            Join the session
          </button>
        ) : view.audit_day ? (
          <button type="button" className="btn" onClick={onBegin}>
            Begin audit session
          </button>
        ) : null}
        <button type="button" className="btn sec" onClick={onDecline}>
          I cannot proceed remotely
        </button>
      </div>
    </div>
  );
}

/**
 * One drop, everything at once — M15-1, ADR 0025.
 *
 * ⚠ **This screen used to ask the client to file their own evidence.** Every expected
 * document was a slot with its own file picker, an upload named the slot it answered, and
 * the object landed in a folder named for that clause. Retrieval decides what a document is
 * evidence of now, per turn, on the audit day — so the client is asked for their documents
 * and not for an index of them.
 *
 * Staging survives from the previous version and for the same reason: nothing reaches the
 * server until Submit, so the choice stays reversible. Firing on the picker's `change` event
 * ticked the counter the moment a file was chosen and there was no undo.
 *
 * The refusals are per file and rendered here, because the server no longer fails the batch
 * over one of them.
 */
function Documents({
  view,
  onChanged,
  onError,
}: {
  view: PortalView;
  onChanged: (view: PortalView) => void;
  onError: (message: string) => void;
}) {
  const [staged, setStaged] = useState<{ id: string; file: File }[]>([]);
  const [refusals, setRefusals] = useState<UploadOutcome[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);

  const stage = (chosen: FileList | null) => {
    if (!chosen || chosen.length === 0) return;
    // Snapshotted *before* `setStaged` is called, and never inside its updater. `chosen` is
    // a live reference to `input.files` — the same object the line below empties — and
    // React does not always invoke a functional updater in the same tick it is scheduled.
    // Reading `chosen` from inside the updater worked the first time, when React's "eager
    // state" fast path ran it synchronously, and silently stopped working on every upload
    // after the first: by the time a deferred updater ran, `picker.current.value = ""` had
    // already cleared this same `FileList` out from under it, so `Array.from(chosen)` read
    // zero files with no error anywhere.
    const picked = Array.from(chosen).map((file, index) => ({
      id: `${file.name}-${staged.length + index}-${file.lastModified}-${Date.now()}`,
      file,
    }));
    setStaged((current) => [...current, ...picked]);
    // Reset so re-picking the same filename fires `change` again — otherwise the browser
    // considers the value unchanged and the handler never runs.
    if (picker.current) picker.current.value = "";
  };

  const remove = (id: string) =>
    setStaged((current) => current.filter((entry) => entry.id !== id));

  const submit = async () => {
    if (staged.length === 0 || submitting) return;
    setSubmitting(true);
    setRefusals([]);
    try {
      const result = await uploadPortalDocuments(staged.map((entry) => entry.file));
      onChanged(result.portal);
      setRefusals((result.results ?? []).filter((entry) => !entry.accepted));
      // Only the accepted ones leave the tray. A file the server refused stays in front of
      // the client with the reason beside it, so they can replace it rather than discover
      // later that it never arrived.
      const kept = new Set(
        (result.results ?? []).filter((entry) => !entry.accepted).map((e) => e.filename),
      );
      setStaged((current) => current.filter((entry) => kept.has(entry.file.name)));
      if (picker.current) picker.current.value = "";
    } catch (cause) {
      onError(problem(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const sent = view.documents ?? [];
  const expected = view.expected ?? [];

  return (
    <div className="card">
      <h2>Your documents — {view.documents_received} sent</h2>
      <p className="sub" style={{ marginBottom: "12px" }}>
        Send everything relevant to the audit in one go. You do not need to say which clause
        each document answers — Complao reads them against the standard on the audit day. You
        can add more at any point, including during the live session; anything you submit is
        logged in the document register and forms part of the audit record.
      </p>

      <input
        type="file"
        multiple
        hidden
        ref={picker}
        onChange={(event) => stage(event.target.files)}
      />
      <div className="row" style={{ gap: "10px", alignItems: "center" }}>
        <button
          type="button"
          className="btn sec"
          disabled={submitting}
          onClick={() => picker.current?.click()}
        >
          Choose documents
        </button>
        <span className="sub" style={{ fontSize: "11px" }}>
          PDF, DOCX, XLSX, TXT or a photo (JPEG, PNG, GIF, WEBP, HEIC) · up to 25 MB each,
          50 at a time
        </span>
      </div>

      {staged.map((entry) => (
        <div className="docrow" key={entry.id}>
          <div className="g">
            <small>Ready to submit — {entry.file.name}</small>
          </div>
          <button
            type="button"
            className="btn sec sm"
            disabled={submitting}
            onClick={() => remove(entry.id)}
          >
            Remove
          </button>
        </div>
      ))}

      {refusals.length > 0 ? (
        <Callout tone="warn" title="These were not accepted.">
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            {refusals.map((entry) => (
              <li key={entry.filename}>
                <b>{entry.filename}</b> — {entry.refused}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <div className="hr" />
      <div className="row" style={{ justifyContent: "flex-end", gap: "10px" }}>
        <span className="sub">
          {staged.length === 0
            ? "Nothing to submit yet."
            : `${staged.length} document${staged.length === 1 ? "" : "s"} ready to submit.`}
        </span>
        <button
          type="button"
          className="btn"
          disabled={staged.length === 0 || submitting}
          onClick={() => void submit()}
        >
          {submitting
            ? "Submitting…"
            : staged.length === 0
              ? "Submit documents"
              : `Submit ${staged.length} document${staged.length === 1 ? "" : "s"}`}
        </button>
      </div>

      {sent.length > 0 ? (
        <>
          <div className="hr" />
          <label className="f">Sent</label>
          {sent.map((document) => (
            <div className="docrow" key={document.id}>
              <div className="g">
                {document.filename}
                <small>
                  {new Date(document.uploaded_at).toLocaleString()}
                  {/* ⚠ A knowledge base does not OCR. A scan ingests as zero retrievable
                      text, successfully and silently, and the clause it was evidence for
                      then reads unevidenced with nothing saying why. */}
                  {document.extraction_ok === false
                    ? " · no readable text found — if this is a scan, a text copy would be read"
                    : ""}
                </small>
              </div>
              <span className="pill p-ok">Sent</span>
            </div>
          ))}
        </>
      ) : null}

      {expected.length > 0 ? (
        <>
          <div className="hr" />
          <label className="f">What an audit of this standard usually looks at</label>
          <p className="sub" style={{ fontSize: "11px", marginBottom: "8px" }}>
            A guide to what to gather, not a checklist. Nothing here is outstanding and you
            are not required to send a document for every line.
          </p>
          {expected.map((row) => (
            <div className="docrow" key={row.item_id}>
              <span className="mono" style={{ width: "62px", flex: "none" }}>
                Cl. {row.clause}
              </span>
              <div className="g">
                {row.name}
                {row.standard_specific ? (
                  <span className="sub"> ({row.standards.join(", ")})</span>
                ) : null}
                <small>{row.stage1_question}</small>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

/** The approved plan, client view. No hours, no percentages, no adjustments. */
function ApprovedPlan({ view }: { view: PortalView }) {
  const plan = view.plan;
  if (!plan) return null;

  return (
    <div className="card">
      <h2>Approved audit plan</h2>
      <dl className="kv">
        <dt>Objectives</dt>
        <dd>{plan.objectives}</dd>
        <dt>Scope</dt>
        <dd>{plan.scope}</dd>
        <dt>Criteria</dt>
        <dd>{plan.criteria}</dd>
        <dt>Exclusions</dt>
        <dd>{plan.exclusions}</dd>
        <dt>Lead auditor</dt>
        <dd>{plan.lead_auditor ?? "—"}</dd>
        <dt>Method</dt>
        <dd>{plan.method}</dd>
      </dl>

      <div className="hr" />
      <div className="eyebrow">Agenda</div>
      <div className="tscroll">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Activity</th>
              <th>Focus</th>
            </tr>
          </thead>
          <tbody>
            {plan.days.map((day) => (
              <tr key={`head-${day.number}`}>
                <td
                  colSpan={3}
                  style={{
                    background: "var(--deep-forest)",
                    color: "var(--amber-wash)",
                    fontSize: "10px",
                    letterSpacing: ".09em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Day {day.number}
                  {day.day_date ? ` · ${day.day_date}` : ""}
                </td>
              </tr>
            ))}
            {plan.days.flatMap((day) =>
              day.rows.map((row, index) => (
                <tr
                  key={`${day.number}-${row.key}-${index}`}
                  style={row.counted ? undefined : { opacity: 0.6 }}
                >
                  <td className="mono" style={{ whiteSpace: "nowrap" }}>
                    {row.starts_at} – {row.ends_at}
                  </td>
                  <td>
                    {row.counted ? (
                      <b>
                        {row.kind === "clause" ? `Cl. ${row.key} — ` : ""}
                        {row.title}
                      </b>
                    ) : (
                      <span className="sub">{row.title}</span>
                    )}
                  </td>
                  <td className="sub">{row.method}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
      <p className="sub" style={{ marginTop: "12px" }}>
        Time allocations and commercial terms are not shown in the client view.
      </p>
    </div>
  );
}

/**
 * Screen 51 — Cl. 4.1.3 and 4.1.4.
 *
 * Two things happen and not one: the remote session stops, and the audit is routed to
 * another method. A dialog that only stopped the session would leave the client with no
 * audit, and the screen says plainly that this is not a failure.
 */
function DeclineDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = reason.trim().length > 0 && reason.trim().length < 10;

  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Unable to proceed remotely</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          IAF MD 4:2025 Cl. 4.1.3 and 4.1.4 require that where agreement on remote conduct is
          not reached, the certification body uses other methods to meet the audit
          objectives. Telling us now stops the session and notifies your lead auditor and
          your certification body.
        </p>

        <TextAreaField
          id="decline-reason"
          label="What prevents you proceeding?"
          value={reason}
          onChange={setReason}
          required
          rows={4}
          placeholder="e.g. Our information security policy does not permit sharing these documents through a third-party platform"
          error={tooShort ? "Please give at least ten characters." : undefined}
        />

        <Callout tone="warn" title="This is not a failure.">
          Declining remote conduct does not affect your certification. The audit is
          rescheduled by another method.
        </Callout>

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Go back
          </button>
          <button
            type="button"
            className="btn danger"
            disabled={busy || reason.trim().length < 10}
            onClick={() => {
              setBusy(true);
              void onConfirm(reason).finally(() => setBusy(false));
            }}
          >
            Confirm — cannot proceed remotely
          </button>
        </div>
      </div>
    </div>
  );
}

function problem(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : "The audit portal could not be read.";
}
